import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState, memo } from "react";
import { motion } from "framer-motion";
import { fetchCreateNameCollection } from "../utils/apis";
import {
  sha3_256_hash_string,
  HASH_ALGORITHM_SHA3_256,
} from "../utils/cryptoUtils";
import { Text, Flex, Box, Modal, useMantineTheme } from "@mantine/core";
import CustomButton from "./mantineCustomComponents/CustomButton";
import { formatHexStr } from "../utils/formatHexStr";
import { useStylesCollectionWindow } from "../utils/mantine/useStylesCollectionWindow";
import TipTapEditor from "./user-profile/TipTapEditor";

const CollectionCreatingModal = ({
  titleConfirmation = "",
  textConfirmation = "",
  titleLoading = "",
  textLoading = "",
  nameCollection = "",
  onNameCollectionError,
  onNameCollectionSuccess,
  onNameCollection = "",
  onShowConfirmationCollectionCreate,
  onUpdateStampQuota,
  opened,
  close,
  form,
}) => {
  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  // Confirmation create Collection an show loading
  const [loading, setLoading] = useState(false);

  // Use custom styles for the Collection Window
  const { classes: useStylesCollectionWindowClasses } =
    useStylesCollectionWindow();

  // Memoize the callback function to avoid unnecessary re-renders
  const handleLoading = useCallback((loading) => {
    setLoading(loading);
  }, []);

  const [isMobile, setIsMobile] = useState(false);

  // Local state for description so typing/paste don't trigger parent (UserProfile) re-renders
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (opened) {
      setDescription(form.getValues().collectionDescription || "");
    }
  }, [opened, form]);

  useEffect(() => {
    // Function to update `isMobile` state based on screen width
    const updateScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Add event listener for screen resizing
    window.addEventListener("resize", updateScreenSize);

    // Set the initial value for `isMobile`
    updateScreenSize();

    // Clean up event listener on component unmount
    return () => window.removeEventListener("resize", updateScreenSize);
  }, []);

  const hashCollectionSha3 = sha3_256_hash_string(nameCollection);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const requestData = {
        name: nameCollection,
        description,
      };
      await fetchCreateNameCollection(
        requestData,
        onNameCollectionError,
        onNameCollectionSuccess,
        onNameCollection,
        onShowConfirmationCollectionCreate,
        onUpdateStampQuota,
        handleLoading,
        close,
        form,
      );
    },
    [
      nameCollection,
      description,
      onNameCollectionError,
      onNameCollectionSuccess,
      onNameCollection,
      onShowConfirmationCollectionCreate,
      onUpdateStampQuota,
      handleLoading,
      close,
      form,
    ],
  );

  return (
    <Modal
      opened={opened}
      onClose={close}
      centered
      size="72em"
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 3,
      }}
      className={useStylesCollectionWindowClasses.collectionWindow}
    >
      {loading && (
        <Flex justify={theme.flex.justify.center} className="loading-wrap">
          <motion.span
            className="loading"
            animate={{
              rotate: 360,
            }}
            transition={{
              repeat: Infinity,
              duration: 1,
              ease: "linear",
            }}
          />
        </Flex>
      )}

      {loading ? (
        <>
          <Flex justify={theme.flex.justify.center}>
            <Text
              classNames={{
                root: "collection-names-button loading-title",
              }}
            >
              {titleLoading}
            </Text>
          </Flex>
          <Text
            classNames={{
              root: "collection-names-button loading-confirmation-text",
            }}
          >
            {" "}
            {textLoading}
          </Text>
        </>
      ) : (
        <>
          <Flex justify={theme.flex.justify.center}>
            <Text
              classNames={{
                root: "confirmation-title",
              }}
            >
              {titleConfirmation}
            </Text>
          </Flex>
          <Box>
            <Text
              component="span"
              classNames={{
                root: "key",
              }}
            >
              Collection Name:
            </Text>
            <Text
              component="span"
              classNames={{
                root: "modal-collection-name value",
              }}
            >
              {" "}
              {nameCollection}
            </Text>
          </Box>
          <Box>
            <Text
              component="span"
              classNames={{
                root: "key",
              }}
            >
              Collection ID ({HASH_ALGORITHM_SHA3_256}):
            </Text>
            <Text
              component="span"
              classNames={{
                root: "value",
              }}
            >
              {isMobile ? (
                <> {formatHexStr(hashCollectionSha3, 6, 4, 13)}</>
              ) : (
                <> {hashCollectionSha3}</>
              )}
            </Text>
          </Box>

          <form onSubmit={handleSubmit}>
            <Text className="text" pt={theme.padding.baseEm}>
              Description (Optional) – this information does not get put on a
              blockchain. It is hosted by vBase and can be edited and deleted
              later.
            </Text>
            <TipTapEditor
              className="editor"
              placeholder="Description"
              popup={true}
              value={description}
              onChange={setDescription}
              error={form.errors.collectionDescription || ""}
            />
            <Text
              classNames={{
                root: "loading-confirmation-text",
              }}
            >
              {" "}
              {textConfirmation}
            </Text>

            <Flex justify={theme.flex.justify.center}>
              <Text
                classNames={{
                  root: "create-text",
                }}
              >
                Create Collection?
              </Text>
            </Flex>
            <Flex
              justify={theme.flex.justify.center}
              gap={theme.flex.gap.large}
              mb={theme.margin.xl}
            >
              <CustomButton
                text="Yes"
                componentClasses="buttonBlack modal-button-submit"
              ></CustomButton>
              <CustomButton
                text="No"
                componentClasses="buttonWhite"
                type="button"
                onClick={() => close()}
              ></CustomButton>
            </Flex>
          </form>
        </>
      )}
    </Modal>
  );
};

CollectionCreatingModal.propTypes = {
  titleConfirmation: PropTypes.string.isRequired,
  textConfirmation: PropTypes.string.isRequired,
  titleLoading: PropTypes.string.isRequired,
  textLoading: PropTypes.string.isRequired,
  nameCollection: PropTypes.string.isRequired,
  onNameCollectionError: PropTypes.func.isRequired,
  onNameCollectionSuccess: PropTypes.func.isRequired,
  onNameCollection: PropTypes.func.isRequired,
  onShowConfirmationCollectionCreate: PropTypes.func.isRequired,
  onUpdateStampQuota: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  open: PropTypes.func,
  close: PropTypes.func.isRequired,
  opened: PropTypes.bool.isRequired,
};

export default memo(CollectionCreatingModal);
